// src/pages/SchemeGrainStorage.js
// ✅ Ready-to-paste (.js)
// ✅ Premium UI + AdSense-friendly layout + 3 languages (EN/HI/GU) with word-to-word matching
// ✅ Includes PMC (your) support offering + clear CTA
// ✅ No guideline distortion: only structured presentation + exact numbers kept

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const cx = (...xs) => xs.filter(Boolean).join(" ");

const STORAGE_KEY = "scheme_lang_grain_storage"; // page-specific

// ✅ Word-to-word: same structure, same points, same numbers across EN/HI/GU
const I18N = {
  en: {
    langName: "English",
    pageTitle: "World’s Largest Grain Storage Plan in Cooperative Sector",
    subTitle: "Sahakar se Samriddhi | Government of India (MoC) | PACS-focused infrastructure",
    badge1: "Guideline-Based",
    badge2: "AdSense-Friendly",
    badge3: "3-Language",
    stickyNote:
      "This page is a structured, simplified presentation strictly based on the official Margdarshika/SOP & Annexures. Final approvals remain with competent authorities.",
    pmcRibbon: "We provide PMC support for end-to-end execution",
    heroH1: "Build PACS Godowns with Assured Hiring, Subsidy & Bank Support",
    heroP:
      "This initiative enables decentralized grain storage and allied agri-infrastructure at PACS level through convergence of existing Government schemes (AIF, AMI, SMAM, PMFME).",
    ctaPrimary: "Start Application / Register Interest",
    ctaSecondary: "Download / View Guidelines",
    quickFactsTitle: "Scheme at a glance",
    facts: [
      { k: "Target level", v: "PACS (Primary Agricultural Credit Societies)" },
      { k: "Key subsidy", v: "AMI subsidy up to 33.33% (PACS eligible)" },
      { k: "Margin money", v: "10%" },
      { k: "Interest support", v: "AIF: 3% p.a. interest subvention (effective ~1% possible with refinance)" },
      { k: "Hiring assurance", v: "MoU between MoC, DFPD, FCI & NCDC for assured utilization" },
      { k: "Timeline", v: "Approx. 8–9 months (Scenario-I/II, subject to approvals)" },
    ],

    // Sections
    tocTitle: "On this page",
    toc: [
      "What is this scheme?",
      "Who can apply?",
      "Key benefits (as per guidelines)",
      "Financial support (AIF/AMI/SMAM/PMFME)",
      "Hiring assurance mechanism",
      "DCP vs Non-DCP states",
      "WDRA regulations & relaxations",
      "Implementation timeline (Scenario-I & II)",
      "Monitoring mechanism",
      "Storage gap context (FCI)",
      "Transportation cost logic (Triangulation)",
      "DCDC checklist: what is evaluated",
      "How we support as PMC (end-to-end)",
      "FAQs",
      "Disclaimer",
    ],

    s1Title: "What is this scheme?",
    s1P1:
      "The World’s Largest Grain Storage Plan in Cooperative Sector is an initiative to create decentralized agricultural infrastructure at PACS level, including godowns, procurement facilities and allied infrastructure by convergence of existing Government of India schemes.",
    s1P2:
      "The plan focuses on smooth implementation, standardized branding & quality assurance, assured hiring/utilization and compliance with warehousing norms, enabling PACS viability and farmer support.",

    s2Title: "Who can apply?",
    s2P: "Eligible beneficiaries (as per the guideline framework) include:",
    s2List: ["PACS", "Cooperative Societies / Federations", "FPOs", "SHGs", "Government agencies (where applicable)"],
    s2Note:
      "Selection/approval is routed through committees and viability parameters such as land availability, financial viability and demand for storage.",

    s3Title: "Key benefits (as per guidelines)",
    s3Cards: [
      {
        t: "Subsidy & interest support",
        p: "AMI capital subsidy (PACS eligible up to 33.33%) + AIF interest subvention (3% p.a.).",
      },
      { t: "Assured hiring / utilization", p: "Hiring assurance mechanism through MoU & advisory directions." },
      { t: "Standardization & trust", p: "Uniform branding, visibility and quality assurance norms." },
      { t: "WDRA enablement", p: "Norms and special relaxations for PACS godowns, enabling NWR ecosystem." },
    ],

    s4Title: "Financial support (AIF / AMI / SMAM / PMFME)",
    s4Intro:
      "The plan uses approved outlays of existing central schemes. There is no additional budget allocation by MoC for the plan.",
    s4TableTitle: "Convergence snapshot (from guideline summary)",
    s4Rows: [
      { scheme: "AIF", type: "Interest subvention", support: "3% interest subvention" },
      { scheme: "AMI", type: "Subsidy", support: "33.33% subsidy (PACS eligible) / cost norms apply" },
      { scheme: "SMAM", type: "Subsidy", support: "40% subsidy on purchase of machines & equipment (as applicable)" },
      { scheme: "PMFME", type: "Subsidy", support: "35% subsidy (as applicable)" },
      {
        scheme: "Grain Storage (refinance)",
        type: "Refinance to DCCB/StCB",
        support: "Refinance @ 3% by NABARD supporting PACS loan @ ~1% (as per guideline note)",
      },
    ],
    s4NormsTitle: "AMI subsidy norms (key points)",
    s4Norms: [
      "PACS eligible to avail subsidy up to 33.33%.",
      "Margin money requirement: 10%.",
      "Cost norms vary by region and capacity bands (50–1000 MT and >1000 MT).",
      "Additional subsidy for ancillary facilities may be provided to PACS, limited to maximum 1/3rd of godown subsidy or actual, whichever is lower (PACS only).",
    ],

    s5Title: "Hiring assurance mechanism",
    s5P1:
      "For assured hiring and optimal utilization of PACS-level storage capacity, an MoU is signed between MoC, DFPD, FCI and NCDC. States/UT Food & Civil Supplies Departments issue Hiring Assurance Letters after DCDC/SCDC approval.",
    s5P2:
      "Godowns under the plan receive priority consideration for designation as Fair Price Shops (FPS), procurement centers and storage centers, subject to location and capacity selection through DCDC/SCDC.",
    s5P3:
      "In hiring, reimbursement rates follow Central Warehousing Corporation (CWC) prescribed rates for CAP/Covered storage, and hiring is given overriding priority (as per guideline directions).",

    s6Title: "DCP vs Non-DCP states",
    s6P:
      "Guidelines distinguish Decentralised Procurement (DCP) and Non-DCP States/UTs for hiring and procurement linkage. Hiring and linkages are enabled through FCI/State agencies and committees.",
    s6Chip1: "Gujarat is listed under DCP States/UT.",
    s6Chip2: "Uttar Pradesh is listed under Non-DCP States/UT.",

    s7Title: "WDRA regulations & relaxations",
    s7P:
      "WDRA norms aim to standardize warehousing practices, facilitate accreditation and promote Negotiable Warehouse Receipts (NWRs). For PACS godowns under this plan, special relaxations are provided regarding infrastructural requirements (as per guideline).",
    s7List: [
      "Raised, well-drained site: plinth height of at least 30 cm may be accepted (as per relaxation note).",
      "Minimum capacity for registration of PACS godowns: 100 MT.",
      "Parking and manoeuvring space for vehicles shall not be insisted upon.",
      "Stack plan should be drawn leaving reasonable space for alleyways.",
      "Beyond society secretary, one more staff may be engaged (full/part time) for storage/preservation.",
      "Security guards may be desirable but not insisted upon for registration.",
      "Pucca boundary wall/barbed wire fencing not insisted; however adequate locking/safety/security arrangement required.",
    ],

    s8Title: "Implementation timeline (Scenario-I & Scenario-II)",
    s8P:
      "The guideline provides step-wise timelines. Overall completion is shown as approximately 8–9 months, subject to approvals and on-ground execution.",
    s8TimelineTitle: "Major milestones (high-level)",
    s8Milestones: [
      "DCDC approval",
      "Hiring assurance",
      "MoU signing / DPR preparation & submission",
      "Loan sanction by DCCB/StCB",
      "Tender award & initiation of construction (where applicable)",
      "Completion of construction",
    ],

    s9Title: "Monitoring mechanism",
    s9P:
      "NLCC (national), SCDC (state) and DCDC (district) undertake reviews on a monthly basis. Regular monitoring is also done at MoC level.",

    s10Title: "Storage gap context (FCI)",
    s10P:
      "FCI shared nationwide storage status (2021–22). Out of 737 districts, 403 are storage deficit districts totalling -132.58 LMT. This supports targeted interventions and optimal location identification.",

    s11Title: "Transportation cost logic (Triangulation)",
    s11P:
      "A triangulation method is used to map distances between PACS as procurement center, FPS, and rice mills to identify optimal PACS locations and potential transportation cost savings.",

    s12Title: "DCDC checklist: what is evaluated",
    s12P:
      "The guideline provides an illustrative checklist for DCDC approval and storage assessment including district profile, crop assessment, storage assessment, requirement and approval details.",

    // ✅ PMC section (your service)
    s13Title: "How we support as PMC (end-to-end)",
    s13Intro:
      "If you join with us, we provide PMC support to execute the scheme workflow professionally as per guidelines, including documentation, coordination and tracking.",
    s13Steps: [
      { t: "Eligibility & PACS readiness check", p: "Land, viability, demand, committee requirements; gap mapping & site feasibility." },
      { t: "DCDC/SCDC documentation pack", p: "Checklist preparation, data compilation, and approval facilitation support." },
      { t: "DPR preparation & technical coordination", p: "Site-specific DPR with soil, land survey and cost budgeting support." },
      { t: "Loan & portal support (AIF/AMI etc.)", p: "Application assistance, bank coordination, verification readiness and submission tracking." },
      { t: "Tender & execution monitoring", p: "Procurement/tender facilitation (where applicable), progress monitoring and reporting." },
      { t: "Hiring assurance & operational readiness", p: "Hiring assurance follow-up, WDRA alignment, and commissioning readiness." },
      { t: "Dashboard & transparency updates", p: "Progress updates, milestone tracking and member/community transparency." },
    ],
    s13CTA: "Contact PMC Team / Get a Call Back",

    faqTitle: "FAQs",
    faqs: [
      {
        q: "Can any person apply directly?",
        a: "Applications are routed under eligible entities and the prescribed process. Individuals typically participate through eligible PACS/organizations as per guidelines.",
      },
      {
        q: "Is there any additional budget by MoC?",
        a: "The plan utilizes approved outlays of existing central schemes; no additional budget allocation by MoC is indicated in the guideline.",
      },
      {
        q: "How long does it take?",
        a: "The guideline indicates an overall timeline of approximately 8–9 months (subject to approvals and execution).",
      },
      {
        q: "How is hiring ensured?",
        a: "Hiring assurance is supported through MoU and advisory directions, including hiring assurance letters and priority designation/hiring mechanisms.",
      },
    ],

    disclaimerTitle: "Disclaimer",
    disclaimerP:
      "This page is created for public awareness and structured understanding. It is based on the official guideline/Margdarshika and annexures. Final eligibility, sanctions, subsidy, and hiring decisions are subject to competent authority approvals and applicable rules.",
  },

  hi: {
    langName: "हिंदी",
    pageTitle: "सहकारी क्षेत्र में विश्व की सबसे बड़ी अनाज भंडारण योजना",
    subTitle: "सहकार से समृद्धि | भारत सरकार (MoC) | PACS आधारित अवसंरचना",
    badge1: "दिशानिर्देश आधारित",
    badge2: "AdSense अनुकूल",
    badge3: "3-भाषा",
    stickyNote:
      "यह पेज आधिकारिक मार्गदर्शिका/SOP एवं परिशिष्टों के आधार पर केवल संरचित व सरल प्रस्तुति है। अंतिम स्वीकृतियाँ सक्षम प्राधिकारी द्वारा ही होंगी।",
    pmcRibbon: "हम PMC के रूप में end-to-end कार्यान्वयन सहायता प्रदान करते हैं",
    heroH1: "PACS गोदाम बनाएं: Hiring Assurance, Subsidy और Bank Support के साथ",
    heroP:
      "यह पहल PACS स्तर पर विकेन्द्रित अनाज भंडारण एवं संबद्ध अवसंरचना को भारत सरकार की मौजूदा योजनाओं (AIF, AMI, SMAM, PMFME) के अभिसरण से सक्षम बनाती है।",
    ctaPrimary: "आवेदन शुरू करें / रुचि दर्ज करें",
    ctaSecondary: "Guidelines देखें / डाउनलोड करें",
    quickFactsTitle: "योजना एक नज़र में",
    facts: [
      { k: "लक्षित स्तर", v: "PACS (प्राथमिक कृषि ऋण समितियाँ)" },
      { k: "मुख्य सब्सिडी", v: "AMI सब्सिडी 33.33% तक (PACS पात्र)" },
      { k: "मार्जिन मनी", v: "10%" },
      { k: "ब्याज सहायता", v: "AIF: 3% प्रति वर्ष ब्याज अनुदान (रिफाइनेंस से ~1% संभव)" },
      { k: "Hiring Assurance", v: "MoC, DFPD, FCI एवं NCDC के बीच MoU द्वारा सुनिश्चित उपयोग" },
      { k: "समय-सीमा", v: "लगभग 8–9 माह (Scenario-I/II, स्वीकृति पर निर्भर)" },
    ],

    tocTitle: "इस पेज में",
    toc: [
      "यह योजना क्या है?",
      "कौन आवेदन कर सकता है?",
      "मुख्य लाभ (दिशानिर्देश अनुसार)",
      "वित्तीय सहायता (AIF/AMI/SMAM/PMFME)",
      "Hiring assurance व्यवस्था",
      "DCP बनाम Non-DCP राज्य",
      "WDRA नियम एवं छूट",
      "कार्यान्वयन समय-रेखा (Scenario-I & II)",
      "Monitoring व्यवस्था",
      "Storage gap संदर्भ (FCI)",
      "Transportation cost logic (Triangulation)",
      "DCDC checklist: क्या देखा जाता है",
      "PMC के रूप में हमारी end-to-end सहायता",
      "FAQs",
      "अस्वीकरण",
    ],

    s1Title: "यह योजना क्या है?",
    s1P1:
      "सहकारी क्षेत्र में विश्व की सबसे बड़ी अनाज भंडारण योजना PACS स्तर पर गोदाम, procurement सुविधाएँ तथा संबद्ध अवसंरचना सहित विकेन्द्रित कृषि अवसंरचना के निर्माण हेतु एक पहल है, जो भारत सरकार की मौजूदा योजनाओं के अभिसरण से लागू होती है।",
    s1P2:
      "योजना का फोकस smooth implementation, standardized branding एवं quality assurance, assured hiring/utilization तथा warehousing norms के अनुपालन पर है, जिससे PACS की व्यवहार्यता एवं किसान सहायता सुनिश्चित हो।",

    s2Title: "कौन आवेदन कर सकता है?",
    s2P: "दिशानिर्देश ढांचे के अनुसार पात्र लाभार्थी:",
    s2List: ["PACS", "सहकारी संस्थाएँ/फेडरेशन", "FPOs", "SHGs", "सरकारी एजेंसियाँ (जहाँ लागू)"],
    s2Note:
      "चयन/स्वीकृति समितियों के माध्यम से तथा भूमि उपलब्धता, वित्तीय व्यवहार्यता और भंडारण मांग जैसे मानकों के आधार पर होती है।",

    s3Title: "मुख्य लाभ (दिशानिर्देश अनुसार)",
    s3Cards: [
      { t: "सब्सिडी व ब्याज सहायता", p: "AMI पूंजी सब्सिडी (PACS पात्र 33.33% तक) + AIF ब्याज अनुदान (3% प्रति वर्ष)।" },
      { t: "Assured hiring / utilization", p: "MoU और advisory directions के माध्यम से hiring assurance व्यवस्था।" },
      { t: "Standardization & trust", p: "Uniform branding, visibility एवं quality assurance norms।" },
      { t: "WDRA enablement", p: "PACS गोदामों हेतु नियम व विशेष छूट, NWR इकोसिस्टम सक्षम।" },
    ],

    s4Title: "वित्तीय सहायता (AIF / AMI / SMAM / PMFME)",
    s4Intro:
      "योजना मौजूदा केंद्रीय योजनाओं के स्वीकृत outlays का उपयोग करती है। MoC द्वारा कोई अतिरिक्त बजट आवंटित नहीं है।",
    s4TableTitle: "अभिसरण सार (दिशानिर्देश अनुसार)",
    s4Rows: [
      { scheme: "AIF", type: "ब्याज अनुदान", support: "3% ब्याज अनुदान" },
      { scheme: "AMI", type: "सब्सिडी", support: "33.33% सब्सिडी (PACS पात्र) / cost norms लागू" },
      { scheme: "SMAM", type: "सब्सिडी", support: "मशीनरी/उपकरण खरीद पर 40% सब्सिडी (जहाँ लागू)" },
      { scheme: "PMFME", type: "सब्सिडी", support: "35% सब्सिडी (जहाँ लागू)" },
      { scheme: "Grain Storage (refinance)", type: "DCCB/StCB को रिफाइनेंस", support: "NABARD द्वारा 3% पर रिफाइनेंस, PACS ऋण ~1% समर्थन (दिशानिर्देश नोट अनुसार)" },
    ],
    s4NormsTitle: "AMI सब्सिडी norms (मुख्य बिंदु)",
    s4Norms: [
      "PACS 33.33% तक सब्सिडी हेतु पात्र।",
      "Margin money requirement: 10%।",
      "Cost norms क्षेत्र एवं क्षमता (50–1000 MT और >1000 MT) के अनुसार बदलते हैं।",
      "Ancillary सुविधाओं हेतु अतिरिक्त सब्सिडी PACS को मिल सकती है, अधिकतम गोदाम सब्सिडी के 1/3 तक या वास्तविक, जो कम हो (केवल PACS)।",
    ],

    s5Title: "Hiring assurance व्यवस्था",
    s5P1:
      "PACS स्तर पर विकसित भंडारण क्षमता के assured hiring एवं optimal utilization हेतु MoC, DFPD, FCI और NCDC के बीच MoU है। DCDC/SCDC स्वीकृति के बाद राज्य/UT Food & Civil Supplies Department Hiring Assurance Letters जारी करते हैं।",
    s5P2:
      "योजना अंतर्गत गोदामों को FPS, procurement center एवं storage center designation हेतु प्राथमिकता दी जाती है, जो DCDC/SCDC के चयन (location/capacity) के अधीन है।",
    s5P3:
      "Hiring में reimbursement rates CWC के CAP/Covered storage हेतु निर्धारित rates के अनुसार होंगे और hiring को overriding priority दी जाती है (दिशानिर्देश अनुसार)।",

    s6Title: "DCP बनाम Non-DCP राज्य",
    s6P:
      "Guidelines में DCP और Non-DCP States/UTs के लिए hiring एवं procurement linkage में अंतर दर्शाया गया है। Hiring/linkages FCI/State agencies और committees के माध्यम से सक्षम होते हैं।",
    s6Chip1: "गुजरात DCP States/UT सूची में है।",
    s6Chip2: "उत्तर प्रदेश Non-DCP States/UT सूची में है।",

    s7Title: "WDRA नियम एवं छूट",
    s7P:
      "WDRA norms का उद्देश्य scientific storage, quality maintenance और transparency सुनिश्चित करना है तथा NWR को बढ़ावा देना है। इस योजना के तहत PACS गोदामों के लिए कुछ infrastructural requirements में विशेष छूट दी गई है (दिशानिर्देश अनुसार)।",
    s7List: [
      "ऊँचा, well-drained site: कम-से-कम 30 cm plinth height स्वीकार हो सकती है (छूट नोट अनुसार)।",
      "PACS गोदाम के registration हेतु न्यूनतम क्षमता: 100 MT।",
      "वाहनों हेतु parking/ manoeuvring space पर insist नहीं।",
      "Alleyways हेतु उचित space के साथ stack plan।",
      "Secretary के अतिरिक्त एक staff (full/part time) storage/preservation हेतु।",
      "Security guards desirable, पर registration हेतु अनिवार्य नहीं।",
      "Boundary wall/fencing पर insist नहीं; पर adequate locking/safety/security व्यवस्था आवश्यक।",
    ],

    s8Title: "कार्यान्वयन समय-रेखा (Scenario-I & Scenario-II)",
    s8P:
      "Guideline में step-wise timelines दिए गए हैं। कुल completion लगभग 8–9 माह दर्शायी गयी है, जो approvals और execution पर निर्भर है।",
    s8TimelineTitle: "मुख्य milestones (high-level)",
    s8Milestones: [
      "DCDC approval",
      "Hiring assurance",
      "MoU / DPR preparation एवं submission",
      "DCCB/StCB द्वारा loan sanction",
      "Tender award एवं construction initiation (जहाँ लागू)",
      "Completion of construction",
    ],

    s9Title: "Monitoring व्यवस्था",
    s9P:
      "NLCC (national), SCDC (state) और DCDC (district) मासिक समीक्षा करते हैं। MoC स्तर पर भी नियमित monitoring होती है।",

    s10Title: "Storage gap संदर्भ (FCI)",
    s10P:
      "FCI ने 2021–22 के nationwide storage status के आधार पर 737 जिलों में से 403 को storage deficit districts बताया है, कुल -132.58 LMT। यह targeted interventions एवं optimal location identification में सहायक है।",

    s11Title: "Transportation cost logic (Triangulation)",
    s11P:
      "Triangulation method के माध्यम से PACS (procurement), FPS और mills के बीच distances map कर optimal PACS locations एवं transportation cost saving पहचानने की व्यवस्था दी गई है।",

    s12Title: "DCDC checklist: क्या देखा जाता है",
    s12P:
      "Guideline में DCDC approval हेतु illustrative checklist है, जिसमें district profile, crop assessment, storage assessment, requirement एवं approval details शामिल हैं।",

    s13Title: "PMC के रूप में हमारी end-to-end सहायता",
    s13Intro:
      "जो भी हमारे साथ जुड़ेगा, हम PMC के रूप में guideline के अनुसार end-to-end execution सहायता देंगे—documentation, coordination और tracking सहित।",
    s13Steps: [
      { t: "Eligibility & readiness check", p: "Land, viability, demand, committee requirements; gap mapping & site feasibility." },
      { t: "DCDC/SCDC documentation pack", p: "Checklist preparation, data compilation, approval facilitation support." },
      { t: "DPR & technical coordination", p: "Site-specific DPR with soil, land survey & cost budgeting support." },
      { t: "Loan & portal support", p: "AIF/AMI आदि आवेदन सहायता, bank coordination, verification readiness, tracking." },
      { t: "Tender & execution monitoring", p: "Where applicable tender facilitation, progress monitoring and reporting." },
      { t: "Hiring assurance & readiness", p: "Hiring assurance follow-up, WDRA alignment, commissioning readiness." },
      { t: "Dashboard transparency", p: "Progress updates, milestone tracking, community transparency." },
    ],
    s13CTA: "PMC Team से संपर्क करें / Call Back",

    faqTitle: "FAQs",
    faqs: [
      {
        q: "क्या कोई व्यक्ति सीधे apply कर सकता है?",
        a: "आवेदन पात्र संस्थाओं और निर्धारित प्रक्रिया के माध्यम से होता है। सामान्यतः व्यक्ति पात्र PACS/संस्था के माध्यम से भाग लेते हैं।",
      },
      {
        q: "क्या MoC का अतिरिक्त बजट है?",
        a: "Guideline अनुसार योजना मौजूदा केंद्रीय योजनाओं के approved outlays का उपयोग करती है; MoC द्वारा अतिरिक्त बजट का उल्लेख नहीं है।",
      },
      {
        q: "कितना समय लगता है?",
        a: "Guideline अनुसार कुल timeline लगभग 8–9 माह है (approvals और execution पर निर्भर)।",
      },
      {
        q: "Hiring कैसे सुनिश्चित होती है?",
        a: "MoU, advisory directions, hiring assurance letters और priority designation/hiring mechanisms के माध्यम से।",
      },
    ],

    disclaimerTitle: "अस्वीकरण",
    disclaimerP:
      "यह पेज जन-जागरूकता हेतु है। यह आधिकारिक guideline/मार्गदर्शिका एवं परिशिष्टों पर आधारित है। अंतिम पात्रता, स्वीकृति, सब्सिडी और hiring सक्षम प्राधिकारी एवं लागू नियमों के अधीन है।",
  },

  gu: {
    langName: "ગુજરાતી",
    pageTitle: "સહકારી ક્ષેત્રમાં વિશ્વની સૌથી મોટી અનાજ સંગ્રહ યોજના",
    subTitle: "સહકારથી સમૃદ્ધિ | ભારત સરકાર (MoC) | PACS આધારિત ઈન્ફ્રાસ્ટ્રક્ચર",
    badge1: "માર્ગદર્શિકા આધારીત",
    badge2: "AdSense અનુકૂળ",
    badge3: "3-ભાષા",
    stickyNote:
      "આ પેજ અધિકૃત માર્ગદર્શિકા/SOP અને પરિશિષ્ટોના આધારે માત્ર રચનાત્મક અને સરળ રજૂઆત છે. અંતિમ મંજૂરી સક્ષમ અધિકારી દ્વારા જ થશે.",
    pmcRibbon: "અમે PMC તરીકે end-to-end અમલ સહાય પૂરી પાડીએ છીએ",
    heroH1: "PACS ગોડાઉન બનાવો: Hiring Assurance, Subsidy અને Bank Support સાથે",
    heroP:
      "આ પહેલ PACS સ્તરે વિકેન્દ્રીત અનાજ સંગ્રહ અને સંબંધિત ઈન્ફ્રાસ્ટ્રક્ચરને સરકારની હાલની યોજનાઓ (AIF, AMI, SMAM, PMFME) ના અભિસરણ દ્વારા સક્ષમ બનાવે છે.",
    ctaPrimary: "અરજી શરૂ કરો / રસ નોંધાવો",
    ctaSecondary: "Guidelines જુઓ / ડાઉનલોડ કરો",
    quickFactsTitle: "યોજના એક નજરમાં",
    facts: [
      { k: "લક્ષ્ય સ્તર", v: "PACS (પ્રાથમિક કૃષિ ધિરાણ સહકારી મંડળીઓ)" },
      { k: "મુખ્ય સબ્સિડી", v: "AMI સબ્સિડી 33.33% સુધી (PACS પાત્ર)" },
      { k: "માર્જિન મની", v: "10%" },
      { k: "વ્યાજ સહાય", v: "AIF: 3% પ્રતિ વર્ષ વ્યાજ સબવેન્શન (રિફાઇનાન્સથી ~1% શક્ય)" },
      { k: "Hiring Assurance", v: "MoC, DFPD, FCI અને NCDC વચ્ચે MoU દ્વારા ખાતરીપૂર્વક ઉપયોગ" },
      { k: "સમય-રેખા", v: "લગભગ 8–9 મહિના (Scenario-I/II, મંજૂરી પર આધારિત)" },
    ],

    tocTitle: "આ પેજમાં",
    toc: [
      "આ યોજના શું છે?",
      "કોણ અરજી કરી શકે?",
      "મુખ્ય લાભ (માર્ગદર્શિકા મુજબ)",
      "નાણાકીય સહાય (AIF/AMI/SMAM/PMFME)",
      "Hiring assurance વ્યવસ્થા",
      "DCP અને Non-DCP રાજ્ય",
      "WDRA નિયમો અને છૂટછાટ",
      "અમલ સમય-રેખા (Scenario-I & II)",
      "Monitoring વ્યવસ્થા",
      "Storage gap સંદર્ભ (FCI)",
      "Transportation cost logic (Triangulation)",
      "DCDC checklist: શું ચકાસાય છે",
      "PMC તરીકે અમારી end-to-end સહાય",
      "FAQs",
      "અસ્વીકાર",
    ],

    s1Title: "આ યોજના શું છે?",
    s1P1:
      "સહકારી ક્ષેત્રમાં વિશ્વની સૌથી મોટી અનાજ સંગ્રહ યોજના PACS સ્તરે ગોડાઉન, procurement સુવિધાઓ અને સંબંધિત ઈન્ફ્રાસ્ટ્રક્ચર સહિત વિકેન્દ્રીત કૃષિ ઈન્ફ્રાસ્ટ્રક્ચર સર્જવા માટેની પહેલ છે, જે ભારત સરકારની હાલની યોજનાઓના અભિસરણથી અમલમાં આવે છે.",
    s1P2:
      "યોજનાનો ફોકસ smooth implementation, standardized branding અને quality assurance, assured hiring/utilization તેમજ warehousing normsનું પાલન છે—જેથી PACSની વ્યાવહારિકતા અને ખેડૂત સહાય સુનિશ્ચિત થાય.",

    s2Title: "કોણ અરજી કરી શકે?",
    s2P: "માર્ગદર્શિકા મુજબ પાત્ર લાભાર્થીઓ:",
    s2List: ["PACS", "સહકારી સંસ્થાઓ/ફેડરેશન્સ", "FPOs", "SHGs", "સરકારી એજન્સીઓ (જ્યાં લાગુ)"],
    s2Note:
      "પસંદગી/મંજૂરી સમિતિઓ દ્વારા અને જમીન ઉપલબ્ધતા, નાણાકીય વ્યાવહારિકતા તથા સંગ્રહ માંગ જેવા માપદંડો પરથી થાય છે.",

    s3Title: "મુખ્ય લાભ (માર્ગદર્શિકા મુજબ)",
    s3Cards: [
      { t: "સબ્સિડી અને વ્યાજ સહાય", p: "AMI મૂડી સબ્સિડી (PACS પાત્ર 33.33% સુધી) + AIF વ્યાજ સબવેન્શન (3% પ્રતિ વર્ષ)." },
      { t: "Assured hiring / utilization", p: "MoU અને advisory directions દ્વારા hiring assurance વ્યવસ્થા." },
      { t: "Standardization & trust", p: "Uniform branding, visibility અને quality assurance norms." },
      { t: "WDRA enablement", p: "PACS ગોડાઉન માટે નિયમો અને ખાસ છૂટછાટ, NWR ઇકોસિસ્ટમ સક્ષમ." },
    ],

    s4Title: "નાણાકીય સહાય (AIF / AMI / SMAM / PMFME)",
    s4Intro:
      "યોજના હાલની કેન્દ્રીય યોજનાઓના approved outlays નો ઉપયોગ કરે છે. MoC દ્વારા વધારાનો બજેટ ફાળવાયેલો નથી.",
    s4TableTitle: "અભિસરણ સાર (માર્ગદર્શિકા મુજબ)",
    s4Rows: [
      { scheme: "AIF", type: "વ્યાજ સબવેન્શન", support: "3% વ્યાજ સબવેન્શન" },
      { scheme: "AMI", type: "સબ્સિડી", support: "33.33% સબ્સિડી (PACS પાત્ર) / cost norms લાગુ" },
      { scheme: "SMAM", type: "સબ્સિડી", support: "મશીનો/ઉપકરણ ખરીદ પર 40% સબ્સિડી (જ્યાં લાગુ)" },
      { scheme: "PMFME", type: "સબ્સિડી", support: "35% સબ્સિડી (જ્યાં લાગુ)" },
      {
        scheme: "Grain Storage (refinance)",
        type: "DCCB/StCB ને રિફાઇનાન્સ",
        support: "NABARD દ્વારા 3% પર રિફાઇનાન્સ, PACS લોન ~1% સમર્થન (માર્ગદર્શિકા નોંધ મુજબ)",
      },
    ],
    s4NormsTitle: "AMI સબ્સિડી norms (મુખ્ય મુદ્દા)",
    s4Norms: [
      "PACS 33.33% સુધી સબ્સિડી માટે પાત્ર છે.",
      "Margin money requirement: 10%.",
      "Cost norms પ્રદેશ અને ક્ષમતા (50–1000 MT અને >1000 MT) મુજબ બદલાય છે.",
      "Ancillary સુવિધાઓ માટે વધારાની સબ્સિડી PACSને મળી શકે છે, વધુમાં વધુ ગોડાઉન સબ્સિડીના 1/3 સુધી અથવા વાસ્તવિક—જે ઓછું હોય (માત્ર PACS).",
    ],

    s5Title: "Hiring assurance વ્યવસ્થા",
    s5P1:
      "PACS સ્તરે વિકસિત સંગ્રહ ક્ષમતાના assured hiring અને optimal utilization માટે MoC, DFPD, FCI અને NCDC વચ્ચે MoU છે. DCDC/SCDC મંજૂરી પછી રાજ્ય/UT Food & Civil Supplies Department Hiring Assurance Letters આપે છે.",
    s5P2:
      "આ યોજના હેઠળના ગોડાઉનને FPS, procurement center અને storage center designation માટે પ્રાથમિકતા મળે છે, જે DCDC/SCDC દ્વારા પસંદગી (location/capacity) પર આધારિત છે.",
    s5P3:
      "Hiring માટે reimbursement rates CWC ના CAP/Covered storage prescribed rates મુજબ રહેશે અને hiring ને overriding priority આપવામાં આવે છે (માર્ગદર્શિકા મુજબ).",

    s6Title: "DCP અને Non-DCP રાજ્ય",
    s6P:
      "Guidelines માં DCP અને Non-DCP States/UTs માટે hiring અને procurement linkage માં તફાવત દર્શાવાયો છે. Hiring/linkages FCI/State agencies અને committees દ્વારા સક્ષમ થાય છે.",
    s6Chip1: "ગુજરાત DCP States/UT યાદીમાં છે.",
    s6Chip2: "ઉત્તર પ્રદેશ Non-DCP States/UT યાદીમાં છે.",

    s7Title: "WDRA નિયમો અને છૂટછાટ",
    s7P:
      "WDRA norms નો હેતુ scientific storage, quality maintenance અને transparency સુનિશ્ચિત કરવો તેમજ NWR ને પ્રોત્સાહન આપવો છે. આ યોજનામાં PACS ગોડાઉન માટે કેટલાક infrastructural requirements માં ખાસ છૂટછાટ આપવામાં આવી છે (માર્ગદર્શિકા મુજબ).",
    s7List: [
      "ઉચ્ચ, well-drained site: ઓછામાં ઓછું 30 cm plinth height સ્વીકાર્ય થઈ શકે (છૂટછાટ નોંધ મુજબ).",
      "PACS ગોડાઉનના registration માટે ન્યૂનતમ ક્ષમતા: 100 MT.",
      "વાહન માટે parking/ manoeuvring space પર insist નહીં.",
      "Alleyways માટે યોગ્ય જગ્યા સાથે stack plan.",
      "Secretary સિવાય એક staff (full/part time) storage/preservation માટે.",
      "Security guards desirable, પણ registration માટે ફરજિયાત નહીં.",
      "Boundary wall/fencing પર insist નહીં; પરંતુ adequate locking/safety/security જરૂરી.",
    ],

    s8Title: "અમલ સમય-રેખા (Scenario-I & Scenario-II)",
    s8P:
      "Guideline માં step-wise timelines આપવામાં આવ્યા છે. કુલ completion લગભગ 8–9 મહિના બતાવવામાં આવી છે, જે approvals અને execution પર આધારિત છે.",
    s8TimelineTitle: "મુખ્ય milestones (high-level)",
    s8Milestones: [
      "DCDC approval",
      "Hiring assurance",
      "MoU / DPR preparation અને submission",
      "DCCB/StCB દ્વારા loan sanction",
      "Tender award અને construction initiation (જ્યાં લાગુ)",
      "Completion of construction",
    ],

    s9Title: "Monitoring વ્યવસ્થા",
    s9P:
      "NLCC (national), SCDC (state) અને DCDC (district) દર મહિને સમીક્ષા કરે છે. MoC સ્તરે પણ નિયમિત monitoring થાય છે.",

    s10Title: "Storage gap સંદર્ભ (FCI)",
    s10P:
      "FCI એ 2021–22 ના nationwide storage status મુજબ 737માંથી 403 જિલ્લાઓને storage deficit districts દર્શાવ્યા છે, કુલ -132.58 LMT. આ targeted interventions અને optimal location identification માટે ઉપયોગી છે.",

    s11Title: "Transportation cost logic (Triangulation)",
    s11P:
      "Triangulation method દ્વારા PACS (procurement), FPS અને mills વચ્ચે distances map કરીને optimal PACS locations અને transportation cost saving ઓળખવાનું મોડલ આપવામાં આવ્યું છે.",

    s12Title: "DCDC checklist: શું ચકાસાય છે",
    s12P:
      "Guideline માં DCDC approval માટે illustrative checklist છે જેમાં district profile, crop assessment, storage assessment, requirement અને approval details સામેલ છે.",

    s13Title: "PMC તરીકે અમારી end-to-end સહાય",
    s13Intro:
      "જે અમારા સાથે જોડાશે, અમે PMC તરીકે માર્ગદર્શિકા મુજબ end-to-end અમલ સહાય આપશું—documentation, coordination અને tracking સહિત.",
    s13Steps: [
      { t: "Eligibility & readiness check", p: "Land, viability, demand, committee requirements; gap mapping & site feasibility." },
      { t: "DCDC/SCDC documentation pack", p: "Checklist preparation, data compilation, approval facilitation support." },
      { t: "DPR & technical coordination", p: "Site-specific DPR with soil, land survey & cost budgeting support." },
      { t: "Loan & portal support", p: "AIF/AMI વગેરે અરજી સહાય, bank coordination, verification readiness, tracking." },
      { t: "Tender & execution monitoring", p: "Where applicable tender facilitation, progress monitoring and reporting." },
      { t: "Hiring assurance & readiness", p: "Hiring assurance follow-up, WDRA alignment, commissioning readiness." },
      { t: "Dashboard transparency", p: "Progress updates, milestone tracking, community transparency." },
    ],
    s13CTA: "PMC Team સંપર્ક / Call Back",

    faqTitle: "FAQs",
    faqs: [
      {
        q: "શું કોઈ વ્યક્તિ સીધી અરજી કરી શકે?",
        a: "અરજી પાત્ર સંસ્થાઓ અને નિર્ધારિત પ્રક્રિયા મુજબ થાય છે. સામાન્ય રીતે વ્યક્તિ પાત્ર PACS/સંસ્થા દ્વારા ભાગ લે છે.",
      },
      {
        q: "શું MoC નો વધારાનો બજેટ છે?",
        a: "Guideline મુજબ યોજના હાલની કેન્દ્રીય યોજનાઓના approved outlays નો ઉપયોગ કરે છે; MoC દ્વારા વધારાના બજેટનો ઉલ્લેખ નથી.",
      },
      { q: "કેટલો સમય લાગે છે?", a: "Guideline મુજબ કુલ timeline લગભગ 8–9 મહિના (approvals અને execution પર આધારિત)." },
      {
        q: "Hiring કેવી રીતે સુનિશ્ચિત થાય છે?",
        a: "MoU, advisory directions, hiring assurance letters અને priority designation/hiring mechanisms દ્વારા.",
      },
    ],

    disclaimerTitle: "અસ્વીકાર",
    disclaimerP:
      "આ પેજ જાહેર જાગૃતિ માટે છે. તે અધિકૃત guideline/માર્ગદર્શિકા અને પરિશિષ્ટો પર આધારિત છે. અંતિમ પાત્રતા, મંજૂરી, સબ્સિડી અને hiring સક્ષમ અધિકારી અને લાગુ નિયમો હેઠળ રહેશે.",
  },
};

function AdSlot({ label = "AdSense Ad Slot", className = "" }) {
  return (
    <div
      className={cx(
        "w-full rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-center",
        "text-xs font-extrabold tracking-widest text-slate-500",
        className
      )}
      aria-label={label}
    >
      {label}
      <div className="mt-2 text-[11px] font-semibold tracking-normal text-slate-400">
        (Place your AdSense responsive unit here)
      </div>
    </div>
  );
}

function Section({ id, title, children, icon }) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 grid h-10 w-10 place-items-center rounded-2xl bg-slate-900 text-white shadow-sm">
          <span className="text-lg">{icon}</span>
        </div>
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-black text-slate-900">{title}</h2>
          <div className="mt-4">{children}</div>
        </div>
      </div>
    </section>
  );
}

function FAQ({ items }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="space-y-3">
      {items.map((it, idx) => (
        <div key={idx} className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setOpen(open === idx ? -1 : idx)}
            className="w-full text-left px-5 py-4 flex items-start justify-between gap-4"
          >
            <div className="font-extrabold text-slate-900">{it.q}</div>
            <div className="text-slate-500 font-black">{open === idx ? "−" : "+"}</div>
          </button>
          {open === idx && (
            <div className="px-5 pb-5 text-sm leading-7 text-slate-700">{it.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SchemeGrainStorage() {
  const [lang, setLang] = useState("en");

  // ✅ keep language persistent + compatible with your i18n localStorage key if you use i18next
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) || localStorage.getItem("i18nextLng");
    if (saved && I18N[saved]) setLang(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    // optional: also sync with i18nextLng so your global system stays consistent
    localStorage.setItem("i18nextLng", lang);
  }, [lang]);

  const t = useMemo(() => I18N[lang] || I18N.en, [lang]);

  const tocIds = useMemo(
    () => [
      "s1",
      "s2",
      "s3",
      "s4",
      "s5",
      "s6",
      "s7",
      "s8",
      "s9",
      "s10",
      "s11",
      "s12",
      "s13",
      "faq",
      "disc",
    ],
    []
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Top gradient header */}
      <div className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-slate-900 text-white grid place-items-center font-black">
              GS
            </div>
            <div className="leading-tight">
              <div className="text-sm font-black text-slate-900">{t.pageTitle}</div>
              <div className="text-xs text-slate-500 font-semibold">{t.subTitle}</div>
            </div>
          </div>

          {/* Language Switcher */}
          <div className="flex items-center gap-2">
            {["en", "hi", "gu"].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setLang(k)}
                className={cx(
                  "px-3 py-2 rounded-xl text-sm font-extrabold border transition",
                  lang === k
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                )}
              >
                {I18N[k].langName}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-4 pt-8 pb-6">
        <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(59,130,246,0.12),transparent_55%),radial-gradient(circle_at_90%_20%,rgba(16,185,129,0.12),transparent_55%),radial-gradient(circle_at_30%_90%,rgba(249,115,22,0.10),transparent_55%)]" />
          <div className="relative p-6 md:p-10 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full text-[11px] font-extrabold bg-slate-900 text-white">
                  {t.badge1}
                </span>
                <span className="px-3 py-1 rounded-full text-[11px] font-extrabold bg-emerald-600 text-white">
                  {t.badge2}
                </span>
                <span className="px-3 py-1 rounded-full text-[11px] font-extrabold bg-blue-600 text-white">
                  {t.badge3}
                </span>
              </div>

              <h1 className="mt-4 text-2xl md:text-4xl font-black text-slate-900 leading-tight">
                {t.heroH1}
              </h1>
              <p className="mt-3 text-sm md:text-base leading-7 text-slate-700">
                {t.heroP}
              </p>

              <div className="mt-5 flex flex-wrap gap-3">
                <a
                  href="#s13"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-slate-900 text-white font-extrabold shadow-sm hover:opacity-95"
                >
                  {t.ctaPrimary}
                </a>

                {/* If you have a guideline PDF route or file, update this link */}
                <a
                  href="#s4"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-white border border-slate-200 text-slate-900 font-extrabold hover:bg-slate-50"
                >
                  {t.ctaSecondary}
                </a>

                {/* Optional: internal apply route */}
                <Link
                  to="/apply"
                  className="inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-emerald-600 text-white font-extrabold hover:opacity-95"
                >
                  Apply Form (Optional)
                </Link>
              </div>

              <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-xs leading-6 text-amber-900 font-semibold">
                <span className="font-black">Note:</span> {t.stickyNote}
              </div>

              <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs leading-6 text-blue-900 font-semibold">
                <span className="font-black">PMC:</span> {t.pmcRibbon}
              </div>
            </div>

            {/* Right visual */}
            <div className="relative">
              <div className="rounded-3xl overflow-hidden border border-slate-200 bg-slate-100 shadow-sm">
                {/* ✅ Put your real image path here */}
                <img
                  src="/images/schemes/grain-storage-hero.jpg"
                  alt="Grain Storage Plan"
                  className="w-full h-[260px] md:h-[340px] object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="p-5 bg-white">
                  <div className="text-sm font-black text-slate-900">{t.quickFactsTitle}</div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {t.facts.map((f, idx) => (
                      <div key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                        <div className="text-[11px] font-extrabold tracking-widest text-slate-500">
                          {f.k}
                        </div>
                        <div className="mt-1 text-sm font-extrabold text-slate-900">{f.v}</div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4">
                    <AdSlot label="AdSense Slot (Top - Responsive)" />
                  </div>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-[11px] font-extrabold tracking-widest text-slate-500">
                    PACS / Cooperative
                  </div>
                  <div className="mt-1 text-sm font-extrabold text-slate-900">
                    Godown + Procurement + FPS linkage
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="text-[11px] font-extrabold tracking-widest text-slate-500">
                    Hiring Assurance
                  </div>
                  <div className="mt-1 text-sm font-extrabold text-slate-900">
                    Priority utilization mechanisms
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TOC + Layout */}
        <div className="mt-6 grid lg:grid-cols-[360px_1fr] gap-6">
          {/* Left TOC */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
              <div className="text-sm font-black text-slate-900">{t.tocTitle}</div>
              <div className="mt-4 space-y-2">
                {t.toc.map((label, idx) => (
                  <a
                    key={idx}
                    href={`#${tocIds[idx]}`}
                    className="block rounded-2xl px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 border border-transparent hover:border-slate-200"
                  >
                    {label}
                  </a>
                ))}
              </div>

              <div className="mt-4">
                <AdSlot label="AdSense Slot (Sidebar)" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-10">
            <Section id="s1" title={t.s1Title} icon="📘">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s1P1}</p>
                <p className="mt-3 text-sm leading-7 text-slate-700">{t.s1P2}</p>
              </div>
            </Section>

            <Section id="s2" title={t.s2Title} icon="👥">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s2P}</p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                  {t.s2List.map((x, i) => (
                    <li key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-bold text-slate-800">
                      ✅ {x}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 rounded-2xl border border-slate-200 bg-amber-50 p-4 text-xs leading-6 text-amber-900 font-semibold">
                  {t.s2Note}
                </div>
              </div>
            </Section>

            <AdSlot label="AdSense Slot (In-content)" />

            <Section id="s3" title={t.s3Title} icon="⭐">
              <div className="grid md:grid-cols-2 gap-4">
                {t.s3Cards.map((c, idx) => (
                  <div key={idx} className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                    <div className="text-sm font-black text-slate-900">{c.t}</div>
                    <div className="mt-2 text-sm leading-7 text-slate-700">{c.p}</div>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="s4" title={t.s4Title} icon="💰">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s4Intro}</p>

                <div className="mt-5 rounded-2xl border border-slate-200 overflow-hidden">
                  <div className="bg-slate-900 text-white px-4 py-3 text-sm font-black">
                    {t.s4TableTitle}
                  </div>
                  <div className="divide-y divide-slate-200">
                    {t.s4Rows.map((r, idx) => (
                      <div key={idx} className="grid md:grid-cols-[140px_200px_1fr] gap-3 p-4">
                        <div className="text-xs font-black tracking-widest text-slate-500">{r.scheme}</div>
                        <div className="text-sm font-extrabold text-slate-900">{r.type}</div>
                        <div className="text-sm leading-7 text-slate-700">{r.support}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-sm font-black text-slate-900">{t.s4NormsTitle}</div>
                  <ul className="mt-3 space-y-2">
                    {t.s4Norms.map((x, i) => (
                      <li key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm font-semibold text-slate-800">
                        • {x}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Section>

            <Section id="s5" title={t.s5Title} icon="🧾">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
                <p className="text-sm leading-7 text-slate-700">{t.s5P1}</p>
                <p className="text-sm leading-7 text-slate-700">{t.s5P2}</p>
                <p className="text-sm leading-7 text-slate-700">{t.s5P3}</p>
              </div>
            </Section>

            <Section id="s6" title={t.s6Title} icon="🗺️">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s6P}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="px-3 py-2 rounded-2xl bg-emerald-600 text-white text-sm font-extrabold">
                    {t.s6Chip1}
                  </span>
                  <span className="px-3 py-2 rounded-2xl bg-blue-600 text-white text-sm font-extrabold">
                    {t.s6Chip2}
                  </span>
                </div>
              </div>
            </Section>

            <Section id="s7" title={t.s7Title} icon="🏛️">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s7P}</p>
                <div className="mt-4 grid md:grid-cols-2 gap-3">
                  {t.s7List.map((x, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-7 text-slate-800 font-semibold">
                      {x}
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            <Section id="s8" title={t.s8Title} icon="🧭">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s8P}</p>

                <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-sm font-black text-slate-900">{t.s8TimelineTitle}</div>
                  <div className="mt-3 grid md:grid-cols-3 gap-3">
                    {t.s8Milestones.map((m, idx) => (
                      <div key={idx} className="rounded-2xl border border-slate-200 bg-white p-4">
                        <div className="text-xs font-extrabold tracking-widest text-slate-500">
                          STEP {idx + 1}
                        </div>
                        <div className="mt-1 text-sm font-extrabold text-slate-900">{m}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <AdSlot label="AdSense Slot (Timeline Section)" />
                </div>
              </div>
            </Section>

            <Section id="s9" title={t.s9Title} icon="📊">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s9P}</p>
              </div>
            </Section>

            <Section id="s10" title={t.s10Title} icon="📉">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s10P}</p>
              </div>
            </Section>

            <Section id="s11" title={t.s11Title} icon="🚚">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s11P}</p>
              </div>
            </Section>

            <Section id="s12" title={t.s12Title} icon="✅">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s12P}</p>
                <div className="mt-4 grid md:grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-extrabold tracking-widest text-slate-500">CHECKLIST</div>
                    <div className="mt-1 text-sm font-extrabold text-slate-900">
                      District / Block / Crop / Storage Assessment
                    </div>
                    <div className="mt-1 text-sm text-slate-700 leading-7">
                      Current capacity, usage, gap, and required capacity evaluation.
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="text-xs font-extrabold tracking-widest text-slate-500">APPROVAL</div>
                    <div className="mt-1 text-sm font-extrabold text-slate-900">
                      Hiring assurance + committee approval recording
                    </div>
                    <div className="mt-1 text-sm text-slate-700 leading-7">
                      Approved by DCDC (Yes/No) with reasons and hiring agency details.
                    </div>
                  </div>
                </div>
              </div>
            </Section>

            {/* ✅ PMC Section */}
            <Section id="s13" title={t.s13Title} icon="🛡️">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.s13Intro}</p>

                <div className="mt-5 grid md:grid-cols-2 gap-4">
                  {t.s13Steps.map((s, idx) => (
                    <div key={idx} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                      <div className="text-xs font-extrabold tracking-widest text-slate-500">
                        {idx + 1 < 10 ? `0${idx + 1}` : idx + 1}
                      </div>
                      <div className="mt-1 text-sm font-black text-slate-900">{s.t}</div>
                      <div className="mt-2 text-sm leading-7 text-slate-700">{s.p}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 grid md:grid-cols-[1fr_320px] gap-4 items-start">
                  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                    <div className="text-sm font-black text-emerald-900">PMC Promise</div>
                    <div className="mt-2 text-sm leading-7 text-emerald-900 font-semibold">
                      We work strictly within the Government guideline framework and support PACS/Stakeholders
                      with documentation, coordination, monitoring and milestone tracking.
                    </div>
                  </div>
                  <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="text-sm font-black text-slate-900">{t.s13CTA}</div>
                    <div className="mt-2 text-sm text-slate-700 leading-7">
                      Add your contact flow here (form / WhatsApp / call-back).
                    </div>
                    <div className="mt-4 flex gap-3">
                      <Link
                        to="/contact"
                        className="flex-1 text-center px-4 py-3 rounded-2xl bg-slate-900 text-white font-extrabold hover:opacity-95"
                      >
                        Contact
                      </Link>
                      <a
                        href="https://wa.me/91XXXXXXXXXX"
                        className="flex-1 text-center px-4 py-3 rounded-2xl bg-emerald-600 text-white font-extrabold hover:opacity-95"
                      >
                        WhatsApp
                      </a>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <AdSlot label="AdSense Slot (PMC CTA)" />
                </div>
              </div>
            </Section>

            <Section id="faq" title={t.faqTitle} icon="❓">
              <FAQ items={t.faqs} />
            </Section>

            <Section id="disc" title={t.disclaimerTitle} icon="⚖️">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-5">
                <p className="text-sm leading-7 text-slate-700">{t.disclaimerP}</p>
              </div>
            </Section>

            <AdSlot label="AdSense Slot (Bottom - High Viewability)" className="mb-10" />

            {/* Footer */}
            <div className="pb-14">
              <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-black text-slate-900">{t.pageTitle}</div>
                  <div className="text-xs text-slate-500 font-semibold mt-1">{t.subTitle}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="#s13"
                    className="px-4 py-2 rounded-2xl bg-slate-900 text-white font-extrabold text-sm"
                  >
                    {t.ctaPrimary}
                  </a>
                  <a
                    href="#s4"
                    className="px-4 py-2 rounded-2xl bg-white border border-slate-200 text-slate-900 font-extrabold text-sm"
                  >
                    {t.ctaSecondary}
                  </a>
                </div>
              </div>
              <div className="mt-4 text-center text-xs text-slate-400 font-semibold">
                © {new Date().getFullYear()} — Scheme Information Page (Structured from official guideline)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
